/* assets/section-testimonial-card-carousel.js */

(() => {
  const ROOT = '.testimonial-card-carousel';
  const BODY_LOCK_CLASS = 'testimonial-card-carousel-modal-open';
  const EXIT_DISTANCE = 110;
  const EXIT_DURATION = 0.8;
  const RETURN_DURATION = 0.65;
  const instances = new WeakMap();

  function padIndex(n) {
    return String(n).padStart(2, '0');
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function loadGsap(callback) {
    if (typeof window.gsap !== 'undefined') {
      callback();
      return;
    }

    if (window.__tccGsapLoading) {
      window.__tccGsapLoading.push(callback);
      return;
    }

    window.__tccGsapLoading = [callback];
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js';
    script.onload = () => {
      const queue = window.__tccGsapLoading || [];
      window.__tccGsapLoading = null;
      queue.forEach((fn) => fn());
    };
    document.head.appendChild(script);
  }

  class TestimonialCardCarousel {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-tcc-slide]'));
      this.texts = Array.from(root.querySelectorAll('[data-tcc-text]'));
      this.layers = Array.from(root.querySelectorAll('[data-tcc-layer]'));
      this.card = root.querySelector('[data-tcc-card]');
      this.btnPrev = root.querySelector('[data-tcc-prev]');
      this.btnNext = root.querySelector('[data-tcc-next]');
      this.indexCurrent = root.querySelector('[data-tcc-index-current]');
      this.progress = root.querySelector('.testimonial-card-carousel__progress');
      this.progressFill = root.querySelector('[data-tcc-progress-fill]');
      this.modal = root.querySelector('[data-tcc-modal]');
      this.modalBody = root.querySelector('[data-tcc-modal-body]');
      this.modalClose = root.querySelector('.testimonial-card-carousel__modal-close');

      this.total = this.slides.length;
      this.current = 0;
      this.autoTimer = null;
      this.isAnimating = false;
      this.isModalOpen = false;
      this.lastFocused = null;

      this.cardTilt = parseFloat(root.dataset.cardTilt);
      if (Number.isNaN(this.cardTilt)) this.cardTilt = 0;
      this.layerStep = parseFloat(root.dataset.layerStep);
      if (Number.isNaN(this.layerStep) || this.layerStep <= 0) this.layerStep = 0.6;
      this.exitRotate = parseFloat(root.dataset.exitRotate);
      if (Number.isNaN(this.exitRotate)) this.exitRotate = 6;
      /* Counter-clockwise fan (negative degrees) */
      this.stackDir = -1;

      this.autoCarousel = root.dataset.autoCarousel === 'true';
      this.autoSpeed = parseInt(root.dataset.autoSpeed, 10) || 4000;

      if (this.total === 0) return;

      this.hasGsap = typeof window.gsap !== 'undefined';
      this.root.classList.toggle('is-gsap', this.hasGsap);

      this.bindEvents();
      this.applyStack();
      this.initSlides();
      this.syncMeta();
      this.startAuto();
    }

    bindEvents() {
      this.onPrev = () => this.goTo(this.current - 1);
      this.onNext = () => this.goTo(this.current + 1);
      this.onEnter = () => this.stopAuto();
      this.onLeave = () => {
        if (!this.isModalOpen) this.startAuto();
      };
      this.onVisibility = () => {
        if (document.hidden || this.isModalOpen) this.stopAuto();
        else this.startAuto();
      };
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.isModalOpen) this.closeModal();
      };
      this.onPlayClick = (event) => {
        const trigger = event.target.closest('[data-tcc-play]');
        if (!trigger || !this.root.contains(trigger)) return;
        event.preventDefault();
        this.openModal(trigger);
      };
      this.onModalClick = (event) => {
        if (event.target.closest('[data-tcc-modal-close]')) this.closeModal();
      };

      if (this.btnPrev) this.btnPrev.addEventListener('click', this.onPrev);
      if (this.btnNext) this.btnNext.addEventListener('click', this.onNext);
      this.root.addEventListener('mouseenter', this.onEnter);
      this.root.addEventListener('mouseleave', this.onLeave);
      this.root.addEventListener('click', this.onPlayClick);
      if (this.modal) this.modal.addEventListener('click', this.onModalClick);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('keydown', this.onKeydown);
    }

    /*
     * Stack geometry (shared .frame box):
     * - Cover + outlines are the same size (CSS inset: 0 on both)
     * - Layer angles are RELATIVE to the frame so layer 0 == cover
     * - Outer layers fan CCW; on the last slide every remaining layer is 0° (flush)
     */
    cardAngle() {
      return this.stackDir * this.cardTilt;
    }

    /* Relative to the frame — layer 0 is always 0° so it matches the cover */
    layerAngle(index, visibleCount) {
      if (visibleCount <= 1) return 0;
      return this.stackDir * index * this.layerStep;
    }

    applyStack() {
      const angle = this.cardAngle();
      if (this.card) {
        this.card.style.transform = `rotate(${angle}deg)`;
      }
      this.updateLayerVisibility(false);
    }

    updateLayerVisibility(animate = true) {
      const visibleCount = Math.max(0, this.layers.length - this.current);
      const reduceMotion = prefersReducedMotion();

      this.layers.forEach((layer, i) => {
        const visible = i < visibleCount;
        const angle = this.layerAngle(i, visibleCount);
        layer.classList.toggle('is-hidden', !visible);

        if (!this.hasGsap) {
          layer.style.opacity = visible ? '1' : '0';
          layer.style.visibility = visible ? 'visible' : 'hidden';
          layer.style.transform = `rotate(${angle}deg)`;
          return;
        }

        window.gsap.killTweensOf(layer);
        const props = {
          autoAlpha: visible ? 1 : 0,
          rotate: angle,
        };

        if (animate && !reduceMotion) {
          window.gsap.to(layer, {
            duration: 0.45,
            ease: 'power2.out',
            ...props,
          });
        } else {
          window.gsap.set(layer, props);
        }
      });
    }

    /* —— Slide positions —— */
    initSlides() {
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === this.current);
        this.placeSlide(i, i < this.current, false);
      });
    }

    exitVector(index) {
      const sign = index % 2 === 0 ? 1 : -1;
      return {
        x: `${sign * EXIT_DISTANCE}vw`,
        rotate: sign * this.exitRotate,
      };
    }

    placeSlide(index, isOut, animate, duration, onComplete) {
      const slide = this.slides[index];
      if (!slide) return;

      const target = isOut ? this.exitVector(index) : { x: 0, rotate: 0 };

      if (!this.hasGsap) {
        slide.style.transform = isOut
          ? `translateX(${target.x}) rotate(${target.rotate}deg)`
          : 'translateX(0) rotate(0deg)';
        if (onComplete) onComplete();
        return;
      }

      window.gsap.killTweensOf(slide);

      if (!animate) {
        window.gsap.set(slide, target);
        if (onComplete) onComplete();
        return;
      }

      window.gsap.to(slide, {
        duration: prefersReducedMotion() ? 0 : duration,
        ease: isOut ? 'power2.inOut' : 'power2.out',
        x: target.x,
        rotate: target.rotate,
        onComplete,
      });
    }

    /*
     * Slides sit in a fixed stack (index 0 on top). Everything before the
     * active index is parked off-screen, so a switch only ever animates the
     * single card the viewer can see; the rest are repositioned while covered.
     */
    transitionTo(next) {
      const previous = this.current;
      if (next === previous) return;

      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === next));

      if (next > previous) {
        for (let i = previous + 1; i < next; i += 1) {
          this.placeSlide(i, true, false);
        }
        this.placeSlide(previous, true, true, EXIT_DURATION);
        return;
      }

      const restoreCovered = () => {
        for (let i = next + 1; i < previous; i += 1) {
          this.placeSlide(i, false, false);
        }
      };

      this.placeSlide(next, false, true, RETURN_DURATION, restoreCovered);
    }

    syncMeta() {
      this.texts.forEach((text, i) => {
        text.classList.toggle('is-active', i === this.current);
      });

      if (this.indexCurrent) {
        this.indexCurrent.textContent = padIndex(this.current + 1);
      }

      if (this.progressFill) {
        this.progressFill.style.width = `${((this.current + 1) / this.total) * 100}%`;
      }

      if (this.progress) {
        this.progress.setAttribute('aria-valuenow', String(this.current + 1));
      }

      if (this.btnPrev) {
        const disabled = this.current <= 0 && !this.autoCarousel;
        this.btnPrev.disabled = disabled;
        this.btnPrev.classList.toggle('is-disabled', disabled);
      }

      if (this.btnNext) {
        const disabled = this.current >= this.total - 1 && !this.autoCarousel;
        this.btnNext.disabled = disabled;
        this.btnNext.classList.toggle('is-disabled', disabled);
      }
    }

    goTo(index) {
      if (this.total <= 1 || this.isAnimating) return;

      let next = index;
      if (this.autoCarousel) {
        if (next < 0) next = this.total - 1;
        if (next >= this.total) next = 0;
      } else {
        next = Math.max(0, Math.min(this.total - 1, next));
      }

      if (next === this.current) return;

      this.isAnimating = true;
      this.transitionTo(next);
      this.current = next;
      this.updateLayerVisibility(true);
      this.syncMeta();
      this.stopAuto();
      this.startAuto();

      window.setTimeout(() => {
        this.isAnimating = false;
      }, EXIT_DURATION * 1000 + 40);
    }

    /* —— Video popup —— */
    openModal(trigger) {
      if (!this.modal || !this.modalBody) return;

      const slide = trigger.closest('[data-tcc-slide]');
      const template = slide && slide.querySelector('[data-tcc-video-template]');
      if (!template) return;

      this.lastFocused = trigger;
      this.modalBody.innerHTML = '';
      this.modalBody.appendChild(template.content.cloneNode(true));
      this.modal.removeAttribute('hidden');
      document.body.classList.add(BODY_LOCK_CLASS);
      this.isModalOpen = true;
      this.stopAuto();

      const video = this.modalBody.querySelector('video');
      if (video) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      }

      if (this.modalClose) this.modalClose.focus();
    }

    closeModal() {
      if (!this.modal || !this.isModalOpen) return;

      const video = this.modalBody.querySelector('video');
      if (video) video.pause();

      this.modalBody.innerHTML = '';
      this.modal.setAttribute('hidden', '');
      document.body.classList.remove(BODY_LOCK_CLASS);
      this.isModalOpen = false;

      if (this.lastFocused) {
        this.lastFocused.focus();
        this.lastFocused = null;
      }

      this.startAuto();
    }

    /* —— Autoplay —— */
    startAuto() {
      if (!this.autoCarousel || this.total <= 1 || this.isModalOpen) return;
      this.stopAuto();
      this.autoTimer = window.setInterval(() => {
        const next = this.current + 1 >= this.total ? 0 : this.current + 1;
        this.goTo(next);
      }, this.autoSpeed);
    }

    stopAuto() {
      if (this.autoTimer) {
        window.clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    }

    destroy() {
      this.stopAuto();
      this.closeModal();
      if (this.btnPrev) this.btnPrev.removeEventListener('click', this.onPrev);
      if (this.btnNext) this.btnNext.removeEventListener('click', this.onNext);
      this.root.removeEventListener('mouseenter', this.onEnter);
      this.root.removeEventListener('mouseleave', this.onLeave);
      this.root.removeEventListener('click', this.onPlayClick);
      if (this.modal) this.modal.removeEventListener('click', this.onModalClick);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove(BODY_LOCK_CLASS);
    }
  }

  function initRoot(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, new TestimonialCardCarousel(root));
  }

  function destroyRoot(root) {
    const instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  }

  function resolveRoot(target) {
    if (!target) return null;
    if (target.matches && target.matches(ROOT)) return target;
    return target.querySelector ? target.querySelector(ROOT) : null;
  }

  function initAll() {
    document.querySelectorAll(ROOT).forEach((root) => initRoot(root));
  }

  function boot() {
    loadGsap(initAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    loadGsap(() => {
      const root = resolveRoot(event.target);
      if (root) initRoot(root);
    });
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = resolveRoot(event.target);
    if (root) destroyRoot(root);
  });
})();
